/** @jsxImportSource @compiled/react */
import React, { useState, useEffect, useMemo, useRef, useLayoutEffect, useCallback } from 'react';
import { defineMessages } from 'react-intl-next';
import { graphql } from 'react-relay';

import { cssMap } from '@atlaskit/css';

import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import { useStoreOptions } from '@confluence/content-smartlinks/entry-points/useStoreOptions.ts';
import { isPageSSRd } from '@confluence/content-smartlinks/entry-points/isPageSSRd.ts';
import { ConfluenceSmartCardProvider } from '@confluence/smart-card-provider/entry-points/ConfluenceSmartCardProvider.tsx';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import type { SpaceOverviewSwitcherQuery$data } from '../../__generated__/SpaceOverviewSwitcherQuery.graphql.ts';
import { Card } from '../components/Card.tsx';

import type { TopLinksCardUpdateTopLinksMutation$data } from './__generated__/TopLinksCardUpdateTopLinksMutation.graphql.ts';
import { applyTopLinksPinToggle, insertNewPinnedTopLink } from './applyTopLinksPinToggle.ts';
import type {
	TopLinksCardUpdateSmartSpaceOverviewInput,
	TopLinksCardUpdateTopLinksMutationVariables,
} from './topLinksUpdateMutationInput.types.ts';
import { LinkPickerButton } from './LinkPickerButton.tsx';
import { LinkItem as Item, type LinkData } from './LinkItem.tsx';

const i18n = defineMessages({
	topDocs: {
		id: 'space-overview.smart-space-overview.top-docs-card.title',
		defaultMessage: 'Top docs',
		description: 'Title of the top docs card',
	},
});

type SmartSpaceOverviewFromSwitcher = NonNullable<
	SpaceOverviewSwitcherQuery$data['confluence_smartSpaceOverview']
>;

type TopLinksCardProps = {
	topLinks: SmartSpaceOverviewFromSwitcher['topLinks'];
	spaceKey: string;
};

const topLinksItemWrapperStyles = cssMap({
	itemWrapper: {
		flex: 1,
		minWidth: '0px',
		position: 'relative',
		transformOrigin: '50% 50%',
	},
	itemWrapperHalf: {
		// When there are 4 items, show 2 per row
		flexGrow: 1,
		flexShrink: 1,
		flexBasis: '40%',
		minWidth: '0px',
		position: 'relative',
		transformOrigin: '50% 50%',
	},
});

const TAB_PIN_FLIP_MS = 200;
const TAB_PIN_FLIP_EASING = 'cubic-bezier(0.2, 0.85, 0.3, 1)';
const TAB_PIN_COMPRESS_SCALE = 0.86;

const maxCount = 4;

/** Same URLs (multiset) as `next`, but row order follows `prev`. Refreshes `title` / `isPinned` from `next`. */
function mergeTopLinksPreservingOrder(prev: LinkData[], next: LinkData[]): LinkData[] {
	if (prev.length === 0 || next.length === 0 || prev.length !== next.length) {
		return next;
	}
	const nextByUrl = new Map(next.map((l) => [l.url, l]));
	const prevSig = [...prev.map((l) => l.url)].sort().join('\0');
	const nextSig = [...next.map((l) => l.url)].sort().join('\0');
	if (prevSig !== nextSig) {
		return next;
	}
	return prev.map((p) => {
		const n = nextByUrl.get(p.url);
		if (!n) {
			return p;
		}
		return {
			...p,
			title: n.title,
			isPinned: n.isPinned,
		};
	});
}

/**
 * Builds update input with `topLinks.add` (see `topLinksUpdateMutationInput.types.ts` — Relay artifact
 * input type lags the schema for `topLinks`).
 */
function buildAddTopLinkMutationInput(
	spaceKey: string,
	link: Pick<LinkData, 'url' | 'title' | 'isPinned'>,
): TopLinksCardUpdateSmartSpaceOverviewInput {
	return {
		spaceKey,
		topLinks: {
			add: {
				url: link.url,
				title: link.title,
				isPinned: link.isPinned,
			},
		},
	};
}

/**
 * `input.topLinks` → `edit` → `ConfluenceEditTopLinkInput` (`url`, `isPinned` only — no `title`).
 */
function buildEditTopLinkMutationInput(
	spaceKey: string,
	link: Pick<LinkData, 'url' | 'isPinned'>,
): TopLinksCardUpdateSmartSpaceOverviewInput {
	return {
		spaceKey,
		topLinks: {
			edit: {
				url: link.url,
				isPinned: link.isPinned,
			},
		},
	};
}

/**
 * `input.topLinks` → `remove` → `ConfluenceRemoveTopLinkInput`.
 */
function buildRemoveTopLinkMutationInput(
	spaceKey: string,
	url: string,
): TopLinksCardUpdateSmartSpaceOverviewInput {
	return {
		spaceKey,
		topLinks: {
			remove: { url },
		},
	};
}

const topLinksCardUpdateTopLinksMutation = graphql`
	mutation TopLinksCardUpdateTopLinksMutation(
		$cloudId: ID!
		$input: ConfluenceUpdateSmartSpaceOverviewInput!
	) {
		confluence_updateSmartSpaceOverview(cloudId: $cloudId, input: $input) {
			success
			errors {
				message
			}
			overview {
				topLinks {
					contentId
					lastRefreshed
					links {
						isPinned
						title
						url
					}
				}
			}
		}
	}
`;

export const TopLinksCard = ({ topLinks, spaceKey }: TopLinksCardProps) => {
	const { cloudId } = useSessionData();
	const [commitUpdateTopLinks] = useMutationInterop<
		TopLinksCardUpdateTopLinksMutation$data,
		TopLinksCardUpdateTopLinksMutationVariables
	>(topLinksCardUpdateTopLinksMutation);

	const queryLinks = useMemo<LinkData[]>(() => {
		const rawLinks = topLinks?.links;
		if (!rawLinks) {
			return [];
		}
		const mapped = rawLinks.reduce<LinkData[]>((acc, link) => {
			if (link?.url && link?.title) {
				acc.push({
					id: link.url,
					url: link.url,
					title: link.title,
					isPinned: link.isPinned ?? false,
				});
			}
			return acc;
		}, []);
		return mapped.slice(0, maxCount);
	}, [topLinks]);

	const [links, setLinks] = useState<LinkData[]>(queryLinks);

	const applyTopLinksMutationResponse = useCallback(
		(
			data: TopLinksCardUpdateTopLinksMutation$data | undefined,
			rollback: () => void,
			orderHint?: LinkData[],
		) => {
			if (data?.confluence_updateSmartSpaceOverview?.success) {
				const serverTopLinks = data.confluence_updateSmartSpaceOverview.overview?.topLinks;
				const list = serverTopLinks?.links?.filter(
					(link): link is NonNullable<typeof link> =>
						link != null && link.url != null && link.url !== '' && link.title != null,
				);
				if (list != null && list.length > 0) {
					const fromServer: LinkData[] = list.map((link) => ({
						id: link.url as string,
						url: link.url as string,
						title: link.title as string,
						subtitle: new URL(link.url as string).hostname,
						isPinned: link.isPinned ?? false,
					}));
					const merged =
						orderHint != null ? mergeTopLinksPreservingOrder(orderHint, fromServer) : fromServer;
					setLinks(merged);
				}
				return;
			}
			rollback();
		},
		[setLinks],
	);

	const linksRef = useRef(links);
	linksRef.current = links;

	const itemWrapperRefs = useRef(new Map<string, HTMLDivElement>());
	const prevItemRectsRef = useRef(new Map<string, DOMRectReadOnly>());
	const runTabReorderFlipRef = useRef(false);
	const primaryMotionLinkIdRef = useRef<string | null>(null);

	useEffect(() => {
		setLinks((prev) => mergeTopLinksPreservingOrder(prev, queryLinks));
	}, [queryLinks]);

	const isAtCapacity = links.length >= maxCount;

	const handleAddLink = (linkData: { url: string; title: string }) => {
		const newLink: LinkData = {
			id: `${linkData.url}-${linkData.title}`,
			url: linkData.url,
			title: linkData.title,
			subtitle: new URL(linkData.url).hostname,
			isPinned: true,
		};

		const nextLinks = insertNewPinnedTopLink(links, newLink, maxCount);

		runTabReorderFlipRef.current = true;
		primaryMotionLinkIdRef.current = newLink.id;
		setLinks(nextLinks);

		const variables: TopLinksCardUpdateTopLinksMutationVariables = {
			cloudId,
			input: buildAddTopLinkMutationInput(spaceKey, newLink),
		};

		void commitUpdateTopLinks({ variables })
			.then(({ data }) =>
				applyTopLinksMutationResponse(
					data,
					() => setLinks((prevLinks) => prevLinks.filter((link) => link.id !== newLink.id)),
					nextLinks,
				),
			)
			.catch(() => {
				setLinks((prevLinks) => prevLinks.filter((link) => link.id !== newLink.id));
			});
	};

	const handleRemoveLink = (linkId: string) => {
		const link = links.find((l) => l.id === linkId);
		if (!link) {
			return;
		}
		const previousLinks = links;
		const afterRemove = previousLinks.filter((l) => l.id !== linkId);
		setLinks(afterRemove);

		const variables: TopLinksCardUpdateTopLinksMutationVariables = {
			cloudId,
			input: buildRemoveTopLinkMutationInput(spaceKey, link.url),
		};

		void commitUpdateTopLinks({ variables })
			.then(({ data }) =>
				applyTopLinksMutationResponse(data, () => setLinks(previousLinks), afterRemove),
			)
			.catch(() => {
				setLinks(previousLinks);
			});
	};

	/** Pin and unpin both persist with `topLinks.edit` (`isPinned` true/false). */
	const handlePinLink = (linkId: string) => {
		const previousLinks = links;
		const { nextLinks, didReorder } = applyTopLinksPinToggle(previousLinks, linkId);
		const updatedLink = nextLinks.find((l) => l.id === linkId);
		if (!updatedLink) {
			return;
		}

		if (didReorder) {
			runTabReorderFlipRef.current = true;
			primaryMotionLinkIdRef.current = linkId;
		}
		setLinks(nextLinks);

		const variables: TopLinksCardUpdateTopLinksMutationVariables = {
			cloudId,
			input: buildEditTopLinkMutationInput(spaceKey, updatedLink),
		};

		void commitUpdateTopLinks({ variables })
			.then(({ data }) =>
				applyTopLinksMutationResponse(data, () => setLinks(previousLinks), nextLinks),
			)
			.catch(() => {
				setLinks(previousLinks);
			});
	};

	useLayoutEffect(() => {
		const currentRects = new Map<string, DOMRectReadOnly>();
		for (const link of links) {
			const el = itemWrapperRefs.current.get(link.id);
			if (el) {
				currentRects.set(link.id, el.getBoundingClientRect());
			}
		}

		if (runTabReorderFlipRef.current) {
			runTabReorderFlipRef.current = false;
			const primaryMotionId = primaryMotionLinkIdRef.current;
			const elementsToAnimate = new Set<HTMLDivElement>();
			for (const link of links) {
				const el = itemWrapperRefs.current.get(link.id);
				const prev = prevItemRectsRef.current.get(link.id);
				const curr = currentRects.get(link.id);
				if (!el || !prev || !curr) {
					continue;
				}
				const dx = prev.left - curr.left;
				const dy = prev.top - curr.top;
				if (Math.abs(dx) > 0.5 || Math.abs(dy) > 0.5) {
					const isPrimaryMotionTile = primaryMotionId !== null && link.id === primaryMotionId;
					const compress = isPrimaryMotionTile ? ` scale(${TAB_PIN_COMPRESS_SCALE})` : '';
					el.style.transition = 'none';
					el.style.willChange = 'transform';
					el.style.zIndex = isPrimaryMotionTile ? '3' : '2';
					el.style.transform = `translate(${dx}px, ${dy}px)${compress}`;
					elementsToAnimate.add(el);
				}
			}

			if (elementsToAnimate.size > 0) {
				requestAnimationFrame(() => {
					requestAnimationFrame(() => {
						for (const el of elementsToAnimate) {
							el.style.transition = `transform ${TAB_PIN_FLIP_MS}ms ${TAB_PIN_FLIP_EASING}`;
							el.style.transform = '';
						}

						window.setTimeout(() => {
							for (const el of elementsToAnimate) {
								el.style.transition = '';
								el.style.transform = '';
								el.style.willChange = '';
								el.style.zIndex = '';
							}
							primaryMotionLinkIdRef.current = null;
						}, TAB_PIN_FLIP_MS + 50);
					});
				});
			} else {
				primaryMotionLinkIdRef.current = null;
			}
		}

		prevItemRectsRef.current = currentRects;
	}, [links]);

	const renderTopLinkItemWrapperFull = useCallback((child: React.ReactElement, index: number) => {
		const linkId = linksRef.current[index]?.id ?? (child.key != null ? String(child.key) : '');
		return (
			<div
				key={linkId || (child.key ?? index)}
				css={topLinksItemWrapperStyles.itemWrapper}
				ref={(el) => {
					if (!linkId) {
						return;
					}
					if (el) {
						itemWrapperRefs.current.set(linkId, el);
					} else {
						itemWrapperRefs.current.delete(linkId);
					}
				}}
			>
				{child}
			</div>
		);
	}, []);

	const renderTopLinkItemWrapperHalf = useCallback((child: React.ReactElement, index: number) => {
		const linkId = linksRef.current[index]?.id ?? (child.key != null ? String(child.key) : '');
		return (
			<div
				key={linkId || (child.key ?? index)}
				css={topLinksItemWrapperStyles.itemWrapperHalf}
				ref={(el) => {
					if (!linkId) {
						return;
					}
					if (el) {
						itemWrapperRefs.current.set(linkId, el);
					} else {
						itemWrapperRefs.current.delete(linkId);
					}
				}}
			>
				{child}
			</div>
		);
	}, []);

	const renderTopLinkItemWrapper = isAtCapacity
		? renderTopLinkItemWrapperHalf
		: renderTopLinkItemWrapperFull;

	const initialState = window['__SSR_TOP_DOCS__'];
	const storeData = useStoreOptions(initialState, isPageSSRd());
	const storeOptions = isPageSSRd() ? storeData : {};

	return (
		<ConfluenceSmartCardProvider authFlow="disabled" {...storeOptions}>
			<Card
				title={i18n.topDocs}
				count={links.length}
				maxCount={maxCount}
				renderItemWrapper={renderTopLinkItemWrapper}
				actionButton={
					<LinkPickerButton
						onLinkSubmit={handleAddLink}
						disabled={isAtCapacity}
						existingLinks={links}
					/>
				}
			>
				{links.map((link) => (
					<Item
						key={link.id}
						link={link}
						onRemove={handleRemoveLink}
						onPin={handlePinLink}
						isPinned={link.isPinned}
					/>
				))}
			</Card>
		</ConfluenceSmartCardProvider>
	);
};
